import React, {ChangeEvent, useContext, useEffect, useState} from "react";
import DataDeletionForm from "./model/DataDeletionForm";
import YesOrNoRadioQuestion from "../../framework/component/form/YesOrNoRadioQuestion";
import CheckboxMultiSelect from "../../framework/component/form/CheckboxMultiSelect";
import FormQuestion from "../../framework/component/form/model/FormQuestion";
import ConfirmationArea from "../../framework/component/form/ConfirmationArea";
import {Toast, ToastContainer} from "react-bootstrap";
import {Role} from "../../framework/security/Role";
import AppContext from "../../framework/AppContext";
import apiClient from "../../framework/web/ApiClient";

// UI Constants
const FORM_TITTLE = "Data Destruction Confirmation Form";
const CONFIRMATIONS_NO: string[] = [
  `No data was downloaded in relation to this project.`
];
const CONFIRMATIONS_YES: string[] = [
  `All data supplied by UK Biobank for this research project (including participant-level derived variables) have been securely destroyed.`,
  `All backups of the data (including participant-level derived variables) have been securely destroyed.`,
  `Any hardware on which UK Biobank data (including participant-level derived variables) was stored and which was decommissioned, 
  resold or recycled during the research project was securely destroyed.`,
  `No data (including participant-level derived variables) have been retained, copied, transferred or otherwise stored in 
  any format not explicitly permitted.`,
  `All data destruction has been completed in accordance with the UK Biobank MTA.`
];

const responseMap: Map<number, string> = new Map();

/**
 * This component is the form used to submit and display a data deletion report
 */
export default function DataDeletion() {
  const appContext = useContext(AppContext);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<DataDeletionForm | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [confirmationToShow, setConfirmationToShow] = useState<string[] | null>(null);
  const [parentQuestionAnswers, setParentQuestionAnswers] = useState<Record<string, string>>({}); // used to determine value of mandatory flag
  const [listOfResponsesAsMap, setListOfResponsesAsMap] = useState<Map<number, string>>(new Map()); // formatted ListOfResponses from the db

  const [projectId, instituteId, formId, projectType] = getRequestedReport();

  const UI_API_PATH = `/ams/ui-api/v1/projects/${projectId}/institutes/${instituteId}/data-deletions`;
  const API_PATH = `/ams/api/v1/projects/${projectId}/institutes/${instituteId}/data-deletions`;

  const PROJECT_PATH = projectType === "DATA_ONLY_V2" ? "data-only" : "samples-data";
  const isAmtUser = appContext?.userProfile.isInAnyRole(
      Role.SUPERUSER, Role.SUPER_AMT, Role.SENIOR_AMT, Role.AMT, Role.SYSTEM_ADMIN);
  const REDIRECT_URL = isAmtUser
      ? `/ams/amtProjects/${PROJECT_PATH}/view?applicationId=${projectId}`
      : `/ams/resProjects/${PROJECT_PATH}/details?application-id=${projectId}`;

  // Toast draft saved
  const [successToastMessage, setSuccessToastMessage] = useState<string | null>(null);
  const [errorSavingShow, setErrorSaving] = useState<boolean>(false);

  /**
   * This function wil be triggered every time a radio button is updated.
   * It is a wrapper method for updating the form confirmation buttons and the form selection status
   * @param questionId - the questionId of the question being answered
   * @param selectedValue - the string value of whether the answer has been selected as TRUE/FALSE
   */
  function updateFormPageState(questionId : number, selectedValue: string) {
    updateQuestionSelectionStatus(questionId, selectedValue);
    updateConfirmationsToShow();
  }

  /**
   * This function is responsible for updating the status of the selected parent question and modifying the status of any subquestions relating to that parent question.
   * @param questionId - the questionId of the question being answered
   * @param selectedAnswer - the string value of whether the answer has been selected as TRUE/FALSE
   */
  function updateQuestionSelectionStatus(questionId: number, selectedAnswer: string) {

    if (!questionId) {
      return;
    }

    setParentQuestionAnswers(prev => ({
      ...prev,
      [questionId]: selectedAnswer,
    }))

    // if the parent question is being selected as false, we want to unselect all child checkbox questions that are currently selected.
    if (selectedAnswer === "FALSE") {
      unselectChildCheckboxQuestions(questionId);
    }

    // if the parent question is being selected as true, we want to remove any validation styling and errors so the checkbox questions which show up don't already have an error state
    if (selectedAnswer === "TRUE") {
      const formElement = document.getElementsByClassName('was-validated')[0];
      formElement?.classList.remove('was-validated');
    }
  }

  /**
   * This function will check for any selected checkbox questions belonging to a parent question and unselect them
   * @param questionId - the id of the selected parent question
   */
  function unselectChildCheckboxQuestions(questionId: number) {
    // get list of all subQuestions for this parent question
    const allSubQuestionsForSelectedQuestion = form?.listOfQuestions.find(q => q.questionId === questionId)?.listOfSubQuestions;

    // get a map of all the current responses from the database and also what has been changed on the frontend
    const allResponses = new Map<number, string>([
      ...listOfResponsesAsMap,
      ...responseMap
    ]);

    // get the subQuestions which are checked
    const subQuestionsThatAreCurrentlySelected = allSubQuestionsForSelectedQuestion?.filter(q => allResponses.get(q.questionId) === "TRUE");

    subQuestionsThatAreCurrentlySelected?.forEach(q => {
      // update response map to update value for the backend
      responseMap.set(q.questionId, "FALSE")
      // set selected checkboxes to be unchecked
      const checkboxInput: HTMLInputElement = document.getElementById(`checkboxMultiSelect${q.questionId}Check`) as HTMLInputElement;
      checkboxInput.checked = false;
    });
  }

  /**
   * This function will scan the radios on this page to determine
   * what confirmation buttons must be shown
   */
  async function updateConfirmationsToShow() {
    // Get a list of all the radio buttons that represent a 'Yes' question
    const radios = document.querySelectorAll<HTMLInputElement>(
        'input[type="radio"][id^="singleSelectRadioQuestion"][id$="YesInput"]');

    if (Array.from(radios).some(radio => radio.checked)) {
      // If a single yes is selected then we show the list for yes
      setConfirmationToShow(CONFIRMATIONS_YES);
    } else {
      setConfirmationToShow(CONFIRMATIONS_NO);
    }
  }

  /**
   * Method to get the requested params from the URL.
   *
   * @returns A string array containing 3 values:
   *          <ol>
   *             <li>Application ID</li>
   *             <li>Institute ID</li>
   *             <li>Form ID - <i>This parameter is optional</i></li>
   *             <li>Project Type</li>
   *          </ol>
   */
  function getRequestedReport(): string[] {
    let queryParams = new URLSearchParams(window.location.search);

    // Get params
    let requestedProject = queryParams.get('applicationId');
    let requestedInstitute = queryParams.get('instituteId');
    let requestedReport = queryParams.get('formId');
    let requestedProjectType = queryParams.get('projectType');

    return [requestedProject || "", requestedInstitute || "", requestedReport || "", requestedProjectType || ""];
  }

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        // Prepare request URL
        const response = await apiClient.get(UI_API_PATH, {});

        if (response.status === 200) {
          let formFromResponse: DataDeletionForm = response.data.data as DataDeletionForm;
          // Add a safety check to block AMT from editing the form
          if (!appContext?.userProfile.isInAnyRole(Role.RESEARCHER)) {
            formFromResponse.locked = true;
          }
          setForm(response.data.data);
          return response.data.data;
        } else {
          setError(`Something went wrong. Response Status ${response.status}, Body ${response.statusText}`);
        }
      } catch (error) {
        setError(`Something went wrong. ${error}`);
      }
    };

    fetchFormData().then((initialFormData: DataDeletionForm) => {
      // once we have the data from the backend, we want to update the state to show which radio options have been selected/not selected to check for mandatory status.
      setParentQuestionAnswers(() => {
        // loop through all radio questions from the backend and set them to false initially so we have a map of all questions
        const answers =
            initialFormData?.listOfQuestions
            ?.filter(question => question.type === "MC_SINGLE_SELECT")
            .reduce<Record<string, string>>((acc, question) => {
              acc[question.questionId] = "FALSE";
              return acc;
            }, {}) ?? {};

        // loop through all answers from the backend and overwrite any key value pairs in the map created above
        initialFormData?.listOfResponses?.forEach(response => {
          answers[response.questionId] = response.response;
        });

        return answers;
      });

      // update state containing formatted response data from the backend
      setListOfResponsesAsMap(() => {
        return new Map(initialFormData?.listOfResponses?.map(response => [response.questionId, response.response]))
      })
    });
  }, []);

  /**
   * Method to handle the submission of the form
   *
   * @param event The submit event
   */
  const handleSubmit = (event: any) => {
    setIsSaving(true);

    // Get ID of the button used to trigger the submit so we can direct it to the correct action
    const actionRequested: string = event.nativeEvent.submitter.id;

    // Stop automated page reload
    event.preventDefault();

    switch (actionRequested) {
      case 'dataDeletionUnlockBtn':
        unlockReport().then(() => setIsSaving(false));
        break;
      default:
        // Submit action
        const form = event.currentTarget;
        if (form.checkValidity() === false) {
          form.classList.add('was-validated')
          event.stopPropagation();
          setIsSaving(false);
          return;
        }

        submitForm(responseMap).then(() => setIsSaving(false));
    }
  };

  const unlockReport = async () => {
    try {
      // Prepare request URL
      let requestURL: string = `${API_PATH}/unlock-form`;

      const response = await apiClient.post(requestURL, []);
      if (response.status === 201) {
        console.log("Report Unlocked")
        setSuccessToastMessage(`Form unlocked at ${
            new Date().toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })
        }`);
      } else {
        console.log(`Something went wrong. Response Status ${response.status}, Body ${response.statusText}`);
        setErrorSaving(true);
      }
    } catch (error) {
      console.log(`Failed to fetch menu: ${error}`);
      setErrorSaving(true);
    }
  }

  /**
   * This method will send the request to submit the form
   */
  const submitForm = async (responseMapForRequest: Map<number, string>) => {
    try {
      // Prepare request URL
      let requestURL: string = API_PATH;

      const response = await apiClient.patch(requestURL, Object.fromEntries(responseMapForRequest));
      if (response.status === 201) {
        console.log("Saved")
        setSuccessToastMessage(`Form submitted at ${
            new Date().toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })
        }`);
      } else {
        console.log(`Something went wrong. Response Status ${response.status}, Body ${response.statusText}`);
        setErrorSaving(true);
      }
    } catch (error) {
      console.log(`Failed to fetch menu: ${error}`);
      setErrorSaving(true);
    }
  };

  if (error) {
    return <div>{error}</div>;
  }

  // Flag to only do initial loading once
  if (!form) {
    return <div>Loading...</div>;
  }

  // This will set the initial confirmation to show only when one hasn't already overridden it
  let initialConfirmation: string[] = CONFIRMATIONS_NO;
  if (!confirmationToShow) {
    // Get list of radio questions
    const listOfRadios: number[] = form.listOfQuestions
    .filter(q => q.type === 'MC_SINGLE_SELECT')
    .map((q, i) => q.questionId);

    // Check if at least one of them is answered yes
    initialConfirmation = form?.listOfResponses?.some(
        response => listOfRadios.includes(response.questionId) && response.response === "TRUE")
        ? CONFIRMATIONS_YES : CONFIRMATIONS_NO;
  }

  return <div>
    {/*Static area*/}
    <a href={REDIRECT_URL}
       className="nav-link return-to-landing">
      <i className="bi bi-arrow-left-short"></i>Return to Project
    </a>
    <div id="dataDeletionStaticArea" className="form-static-area">
      <h2
          id="dataDeletionTitle">{FORM_TITTLE}</h2>
      <div id="dataDeletionProjectInfo" className="project_info">
        <div id="dataDeletionProjectInfoStatic" className="project_info_static">
          <h5 id="dataDeletionProjectInfoStaticProjId">Project:</h5>
          <h5 id="dataDeletionProjectInfoStaticInstitute">Institute:</h5>
        </div>
        <div id="dataDeletionProjectInfoDynamic" className="project_info_dynamic">
          <h5 id="dataDeletionProjectInfoDynamicProjId">{form.applicationId} - {form.applicationTitle}</h5>
          <h5 id="dataDeletionProjectInfoDynamicInstitute">{form.instituteId} - {form.instituteName}</h5>
        </div>
      </div>
    </div>
    {/*Question Area*/}
    <form id="dataDeletionForm" className="needs-validation form-question-area"
          onSubmit={(e) => handleSubmit(e)}>
      <h6><b>During your research project was any UK Biobank data (including participant-level derived variables) stored on:</b></h6>
      {form.listOfQuestions.map((question, index) => (
          <div id={`dataDeletionQuestion${index}`}>
            {/*Show main question body*/}
            {question.type === 'MC_SINGLE_SELECT' ?
                <YesOrNoRadioQuestion question={{
                  questionType: question.type,
                  id: question.questionId,
                  index: index,
                  text: question.text,
                  response: form.listOfResponses?.find(q => q.questionId === question.questionId)?.response
                }} locked={form.locked ? form.locked : false} responseMap={responseMap}
                                      ariaControlsId={`dataDeletionSubQuestionsAccordion${index}`}
                                      onRadioSelect={updateFormPageState}/>
                :
                <></>
              // Other question types might be supported in the future
            }
            {/*Subquestion accordion*/}
            {/*conditional show class is used for initially displaying the checkbox multiselect when getting form responses from the backend. it does not get updated again on the frontend*/}
            <div className={`accordion accordion-flush accordion-collapse collapse 
                  ${form.listOfResponses?.find(q => q.questionId === question.questionId)?.response === "TRUE" ? "show" : ""}
                  `}
                 id={`dataDeletionSubQuestionsAccordion${index}`}
                 data-bs-parent={`#dataDeletionSubQuestionsAccordion${index}`}>
              <CheckboxMultiSelect question={{
                questionType: question.type,
                id: question.questionId,
                index: index
              }} listOfOptions={question.listOfSubQuestions?.map((q, index): FormQuestion => (
                  {
                    questionType: q.type,
                    id: q.questionId,
                    index: index,
                    text: q.text,
                    response: form.listOfResponses?.find(q1 => q1.questionId === q.questionId)?.response
                  }
              ))} locked={form.locked ? form.locked : false}
                                   responseMap={responseMap}
                                   mandatory={parentQuestionAnswers[question.questionId?.toString()] === "TRUE"}/>
            </div>
          </div>
      ))}
      {/*Confirmation area*/}
      {(form.locked === null || !form.locked) && appContext?.userProfile.isInAnyRole(Role.RESEARCHER) ?
          <ConfirmationArea listOfConfirmations={confirmationToShow ? confirmationToShow : initialConfirmation}
                            isSubmittingChange={isSaving}
                            enableSaving={false}
                            textToShow={"I certify that:"}/>
          : <div id="dataDeltionSaveArea" className="form-save-area">
            {appContext?.userProfile.isInAnyRole(Role.SUPERUSER, Role.SUPER_AMT, Role.SENIOR_AMT, Role.AMT) &&
                <button id="dataDeletionUnlockBtn" type="submit" className="btn btn-success"
                        formNoValidate disabled={isSaving}>
                  Unlock
                </button>
            }
          </div>}
    </form>
    <ToastContainer id="dataDeletionToastContainer" position="bottom-end" className="p-3 toast-container">
      {/*Saved Draft toast*/}
      <Toast id="dataDeletionSavedToastToast" onClose={() => {
        window.location.href = REDIRECT_URL
      }} show={successToastMessage !== null}
             delay={3000} autohide>
        <Toast.Header id="dataDeletionSavedToastToastHeader">
          <strong id="dataDeletionSavedToastToastHeaderText" className="me-auto"><i
              id="dataDeletionSavedToastToastHeaderIcon"
              className="bi bi-check-circle-fill text-success"/>Form saved</strong>
        </Toast.Header>
        <Toast.Body id="dataDeletionSavedToastToastBody">{successToastMessage}</Toast.Body>
      </Toast>
      {/*Saved Draft toast*/}
      <Toast id="dataDeletionErrorToastToast" onClose={() => setErrorSaving(false)} show={errorSavingShow}
             delay={5000} autohide>
        <Toast.Header id="dataDeletionErrorToastToastHeader">
          <strong id="dataDeletionErrorToastToastHeaderText" className="me-auto"><i
              id="dataDeletionErrorToastToastHeaderIcon"
              className="bi bi-exclamation-circle-fill text-danger"/> Error</strong>
        </Toast.Header>
        <Toast.Body id="dataDeletionErrorToastToastBody">Error saving the form. Please try again
          later</Toast.Body>
      </Toast>
    </ToastContainer>
  </div>
}
